import React, { lazy, Suspense, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from 'react-error-boundary';
import { Button, Dialog, DialogContentType, DialogFooterType, IconNames, ToastVariants } from '@dotcom/crown-ui';
import { setIsToasterOpen, useAppContext } from '../../context/app-context';
import {
  ANIMATION_PREVIEW_RENDER_ERROR_DEDUPE_KEY,
  reportAnimationPreviewErrorOnce,
} from './utils/report-animation-preview-error';

const AnimationPreviewComponent = lazy(() =>
  import('./animation-preview').then((module) => ({
    default: module.AnimationPreview,
  })),
);

const AnimationPreviewLoader = () => <div>Loading</div>;

type AnimationPreviewDialogProps = {
  isOpen: boolean;
  panelImages: string[];
  onClose: () => void;
};

export const AnimationPreviewDialog = ({ isOpen, panelImages, onClose }: AnimationPreviewDialogProps) => {
  const { t } = useTranslation();
  const { appDispatch } = useAppContext();

  const handleAnimationPreviewError = useCallback(
    (error: Error, info: { componentStack?: string | null }) => {
      reportAnimationPreviewErrorOnce(ANIMATION_PREVIEW_RENDER_ERROR_DEDUPE_KEY, error, {
        source: 'animation-preview',
        kind: 'render-error',
        componentStack: info.componentStack ?? undefined,
      });
      onClose();
      setIsToasterOpen(appDispatch, {
        title: t('previewView.animationPreview.unavailableToastTitle'),
        children: t('previewView.animationPreview.unavailableToastDescription'),
        variant: ToastVariants.Warning,
      });
    },
    [appDispatch, onClose, t],
  );

  return (
    <Dialog
      id="play-animation-dialog"
      isOpen={isOpen}
      onClose={onClose}
      closeButtonId="close"
      accentIcon={IconNames.CardFeaturesBold}
      hasGrayBackground
      type={DialogContentType.FeatureModal}
      footerType={DialogFooterType.Passive}
      disableDialogScroll
      cancelButton={
        <Button onClick={onClose} variant="secondary" testId="cancel-button">
          {t('previewView.cancelButton')}
        </Button>
      }
      title={`${t('previewView.previewAnimationModalTitle')}`}
    >
      <Suspense fallback={<AnimationPreviewLoader />}>
        <ErrorBoundary onError={handleAnimationPreviewError} fallbackRender={() => <AnimationPreviewLoader />}>
          <AnimationPreviewComponent panelImages={panelImages} closeDialog={onClose} />
        </ErrorBoundary>
      </Suspense>
    </Dialog>
  );
};
